<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import TargetsAPI from 'dashboard/api/agentTargets';
import { useAlert } from 'dashboard/composables';
import { useTracking } from 'dashboard/composables/useTracking';
import {
  TARGET_METRICS,
  TARGET_PERIOD_TYPES,
  TARGET_TRACKING_EVENTS,
  countBehindPace,
  defaultTargetValue,
  normalizeTargetsResponse,
  periodRangeLabel,
  periodStartFor,
  progressWidth,
  targetDisplayName,
  targetScope,
  todayDateKey,
  trackingPayloadForRow,
} from '../utils/targetManagement';

const { t } = useI18n();
const { track } = useTracking();

const activeTab = ref('agents');
const metric = ref('finished_visits');
const periodType = ref('monthly');
const selectedDate = ref(todayDateKey());
const agentRows = ref([]);
const teamRows = ref([]);
const agentMeta = ref({});
const teamMeta = ref({});
const isLoading = ref(false);
const isSaving = ref(false);
const isForbidden = ref(false);
const errorMessage = ref('');
const editingContext = ref(null);
const targetValue = ref(0);
const modalMetric = ref(metric.value);
const modalPeriodType = ref(periodType.value);
const modalDate = ref(selectedDate.value);
const selectedAgentId = ref('');
const bulkApplyMembers = ref(false);
const modalPanelRef = ref(null);
const pendingConfirmation = ref(null);

const metricOptions = computed(() =>
  TARGET_METRICS.map(value => {
    if (value === 'signed_ccv') {
      return {
        value,
        label: t('TARGET_MANAGEMENT.METRICS.signed_ccv'),
      };
    }
    if (value === 'sent_offers') {
      return {
        value,
        label: t('TARGET_MANAGEMENT.METRICS.sent_offers'),
      };
    }
    return {
      value,
      label: t('TARGET_MANAGEMENT.METRICS.finished_visits'),
    };
  })
);
const periodOptions = computed(() =>
  TARGET_PERIOD_TYPES.map(value => {
    if (value === 'weekly') {
      return {
        value,
        label: t('TARGET_MANAGEMENT.PERIOD_TYPES.weekly'),
      };
    }
    if (value === 'quarterly') {
      return {
        value,
        label: t('TARGET_MANAGEMENT.PERIOD_TYPES.quarterly'),
      };
    }
    return {
      value,
      label: t('TARGET_MANAGEMENT.PERIOD_TYPES.monthly'),
    };
  })
);
const rows = computed(() =>
  activeTab.value === 'agents' ? agentRows.value : teamRows.value
);
const currentMeta = computed(() =>
  activeTab.value === 'agents' ? agentMeta.value : teamMeta.value
);
const behindCount = computed(
  () => countBehindPace(agentRows.value) + countBehindPace(teamRows.value)
);
const isEmpty = computed(() => !isLoading.value && rows.value.length === 0);
const showModal = computed(() => Boolean(editingContext.value));
const modalTitle = computed(() => {
  if (editingContext.value?.mode === 'new-agent') {
    return t('TARGET_MANAGEMENT.MODAL.NEW_AGENT_TITLE');
  }
  if (editingContext.value?.mode === 'bulk') {
    return t('TARGET_MANAGEMENT.MODAL.BULK_TITLE');
  }
  return editingContext.value?.scope === 'team'
    ? t('TARGET_MANAGEMENT.MODAL.TEAM_TITLE')
    : t('TARGET_MANAGEMENT.MODAL.AGENT_TITLE');
});
const agentSelectOptions = computed(() =>
  agentRows.value
    .filter(row => row.user?.id)
    .map(row => ({ value: String(row.user.id), label: targetDisplayName(row) }))
);
const selectedAgentRow = computed(() =>
  agentRows.value.find(
    row => String(row.user?.id) === String(selectedAgentId.value)
  )
);
const modalRow = computed(() => {
  if (editingContext.value?.mode === 'new-agent') {
    return selectedAgentRow.value;
  }
  return editingContext.value?.row;
});
const modalSubjectLabel = computed(() =>
  targetDisplayName(modalRow.value || {})
);
const modalSource = computed(() => modalRow.value?.target?.source || 'default');
const modalDefaultTargetValue = computed(() =>
  defaultTargetValue(modalMetric.value)
);
const modalPeriodStart = computed(() =>
  periodStartFor(modalPeriodType.value, modalDate.value)
);
const modalPeriodRange = computed(() =>
  periodRangeLabel(modalPeriodType.value, modalDate.value)
);
const currentPeriodRange = computed(() =>
  periodRangeLabel(periodType.value, selectedDate.value)
);
const canBulkApplyFromModal = computed(
  () => editingContext.value?.scope === 'team' && modalRow.value?.team?.id
);
const showModalReset = computed(
  () =>
    editingContext.value?.mode !== 'new-agent' &&
    modalRow.value?.target?.id &&
    modalSource.value !== 'default'
);
const pendingConfirmationMessage = computed(() => {
  if (pendingConfirmation.value?.mode === 'bulk') {
    return t('TARGET_MANAGEMENT.ALERTS.BULK_CONFIRM');
  }
  if (pendingConfirmation.value?.mode === 'reset') {
    return t('TARGET_MANAGEMENT.ALERTS.RESET_CONFIRM');
  }
  return '';
});
const summaryCards = computed(() => [
  {
    label: t('TARGET_MANAGEMENT.SUMMARY.AGENTS'),
    value: agentRows.value.length,
  },
  {
    label: t('TARGET_MANAGEMENT.SUMMARY.TEAMS'),
    value: teamRows.value.length,
  },
  {
    label: t('TARGET_MANAGEMENT.SUMMARY.BEHIND'),
    value: behindCount.value,
  },
  {
    label: t('TARGET_MANAGEMENT.SUMMARY.PACE'),
    value: `${currentMeta.value.pace || 0}%`,
  },
]);

const requestParams = () => ({
  metric: metric.value,
  period_type: periodType.value,
  period_start: periodStartFor(periodType.value, selectedDate.value),
  date: selectedDate.value,
});

function hydrateModalState(row = {}, mode = 'single') {
  modalMetric.value = row.target?.metric || metric.value;
  modalPeriodType.value = row.target?.period_type || periodType.value;
  modalDate.value = row.target?.period_start || selectedDate.value;
  selectedAgentId.value = row.user?.id ? String(row.user.id) : '';
  targetValue.value = Number(
    row.target?.target_value || defaultTargetValue(modalMetric.value)
  );
  bulkApplyMembers.value = mode === 'bulk';
}

function sourceBadgeClass(source) {
  if (source === 'manual') {
    return 'bg-sky-50 text-sky-700 ring-sky-100';
  }
  if (source === 'rollup') {
    return 'bg-amber-50 text-amber-700 ring-amber-100';
  }
  return 'bg-emerald-50 text-emerald-700 ring-emerald-100';
}

function paceLabel(row) {
  return row.behind_pace
    ? t('TARGET_MANAGEMENT.TABLE.PACE_BEHIND')
    : t('TARGET_MANAGEMENT.TABLE.PACE_ON_TRACK');
}

function rowPace(row) {
  return row.pace ?? row.target?.pace ?? currentMeta.value.pace ?? 0;
}

function requestConfirmation(mode, row = modalRow.value) {
  pendingConfirmation.value = { mode, row };
}

function clearPendingConfirmation() {
  pendingConfirmation.value = null;
}

async function loadTargets() {
  isLoading.value = true;
  isForbidden.value = false;
  errorMessage.value = '';

  try {
    const [agentsResponse, teamsResponse] = await Promise.all([
      TargetsAPI.agentTargets.index(requestParams()),
      TargetsAPI.teamTargets.index(requestParams()),
    ]);
    const agents = normalizeTargetsResponse(agentsResponse);
    const teams = normalizeTargetsResponse(teamsResponse);
    agentRows.value = agents.rows;
    teamRows.value = teams.rows;
    agentMeta.value = agents.meta;
    teamMeta.value = teams.meta;
  } catch (error) {
    if (error?.response?.status === 401 || error?.response?.status === 403) {
      isForbidden.value = true;
      return;
    }
    errorMessage.value =
      error?.response?.data?.errors?.[0] || t('TARGET_MANAGEMENT.STATE.ERROR');
    useAlert(errorMessage.value);
  } finally {
    isLoading.value = false;
  }
}

function openEditor(row) {
  editingContext.value = {
    mode: 'single',
    scope: targetScope(row),
    row,
  };
  hydrateModalState(row);
}

function openBulkApply(row) {
  editingContext.value = {
    mode: 'bulk',
    scope: 'team',
    row,
  };
  hydrateModalState(row, 'bulk');
}

function openNewTarget() {
  const firstAgent = agentRows.value[0];
  editingContext.value = {
    mode: 'new-agent',
    scope: 'agent',
    row: null,
  };
  hydrateModalState(firstAgent || {}, 'new-agent');
  selectedAgentId.value = firstAgent?.user?.id
    ? String(firstAgent.user.id)
    : '';
}

function closeEditor() {
  editingContext.value = null;
  targetValue.value = 0;
  selectedAgentId.value = '';
  bulkApplyMembers.value = false;
  clearPendingConfirmation();
}

function formPayload(row = modalRow.value) {
  return {
    metric: modalMetric.value,
    period_type: modalPeriodType.value,
    period_start: modalPeriodStart.value,
    date: modalDate.value,
    target_value: Number(targetValue.value || 0),
    ...(row.user ? { user_id: row.user.id } : {}),
    ...(row.team ? { team_id: row.team.id } : {}),
  };
}

async function saveTarget({ confirmedBulk = false } = {}) {
  if (!editingContext.value) return;

  isSaving.value = true;
  try {
    const { scope } = editingContext.value;
    const row = modalRow.value;
    if (!row) {
      useAlert(t('TARGET_MANAGEMENT.ALERTS.SELECT_AGENT'));
      return;
    }

    if (canBulkApplyFromModal.value && bulkApplyMembers.value) {
      if (!confirmedBulk) {
        requestConfirmation('bulk', row);
        return;
      }
      const response = await TargetsAPI.agentTargets.bulkApply({
        agent_target: formPayload(row),
      });
      track(TARGET_TRACKING_EVENTS.bulkApplied, {
        ...trackingPayloadForRow(row, {
          targetValue: Number(targetValue.value || 0),
          applied: response?.data?.applied,
        }),
        teamId: row.team?.id,
      });
      useAlert(t('TARGET_MANAGEMENT.ALERTS.BULK_APPLIED'));
    } else if (scope === 'team') {
      await TargetsAPI.teamTargets.create({ team_target: formPayload(row) });
      track(
        TARGET_TRACKING_EVENTS.set,
        trackingPayloadForRow(row, {
          targetValue: Number(targetValue.value || 0),
        })
      );
      useAlert(t('TARGET_MANAGEMENT.ALERTS.SAVED'));
    } else {
      await TargetsAPI.agentTargets.create({ agent_target: formPayload(row) });
      track(
        TARGET_TRACKING_EVENTS.set,
        trackingPayloadForRow(row, {
          targetValue: Number(targetValue.value || 0),
        })
      );
      useAlert(t('TARGET_MANAGEMENT.ALERTS.SAVED'));
    }
    closeEditor();
    await loadTargets();
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.[0] || t('TARGET_MANAGEMENT.ALERTS.ERROR')
    );
  } finally {
    isSaving.value = false;
  }
}

async function resetTarget(row, { confirmedReset = false } = {}) {
  if (!row.target?.id) return;
  if (!confirmedReset && showModal.value) {
    requestConfirmation('reset', row);
    return;
  }

  isSaving.value = true;
  try {
    const shouldCloseEditor = showModal.value;
    if (targetScope(row) === 'team') {
      await TargetsAPI.teamTargets.delete(row.target.id);
    } else {
      await TargetsAPI.agentTargets.delete(row.target.id);
    }
    track(TARGET_TRACKING_EVENTS.reset, trackingPayloadForRow(row));
    useAlert(t('TARGET_MANAGEMENT.ALERTS.RESET'));
    if (shouldCloseEditor) closeEditor();
    await loadTargets();
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.[0] || t('TARGET_MANAGEMENT.ALERTS.ERROR')
    );
  } finally {
    isSaving.value = false;
  }
}

async function confirmPendingAction() {
  const pending = pendingConfirmation.value;
  if (!pending) return;

  pendingConfirmation.value = null;
  if (pending.mode === 'bulk') {
    await saveTarget({ confirmedBulk: true });
  } else if (pending.mode === 'reset') {
    await resetTarget(pending.row, { confirmedReset: true });
  }
}

function sourceLabel(source) {
  if (source === 'manual') return t('TARGET_MANAGEMENT.SOURCE.manual');
  if (source === 'rollup') return t('TARGET_MANAGEMENT.SOURCE.rollup');
  return t('TARGET_MANAGEMENT.SOURCE.default');
}

function formatPercent(value) {
  return `${Number(value || 0)}%`;
}

function rowActionLabel(row) {
  if (targetScope(row) === 'team' && row.target?.source === 'manual') {
    return t('TARGET_MANAGEMENT.ACTIONS.RESTORE_ROLLUP');
  }
  return t('TARGET_MANAGEMENT.ACTIONS.RESET');
}

function focusableModalElements() {
  if (!modalPanelRef.value) return [];

  return Array.from(
    modalPanelRef.value.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  ).filter(element => !element.getAttribute('aria-hidden'));
}

function focusFirstModalField() {
  focusableModalElements()[0]?.focus?.();
}

function handleModalKeydown(event) {
  if (event.key !== 'Tab') return;

  const focusable = focusableModalElements();
  if (!focusable.length) return;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

function handleGlobalKeydown(event) {
  if (showModal.value && event.key === 'Escape') {
    closeEditor();
  }
}

watch([metric, periodType, selectedDate], loadTargets);
watch(showModal, async value => {
  if (!value) return;

  await nextTick();
  focusFirstModalField();
});

onMounted(() => {
  loadTargets();
  window.addEventListener('keydown', handleGlobalKeydown);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleGlobalKeydown);
});
</script>

<template>
  <section class="flex h-full flex-col overflow-hidden bg-[#fcfaf6]">
    <LaisHeader :title="t('TARGET_MANAGEMENT.TITLE')">
      <template #actions>
        <label
          class="flex flex-col gap-1 text-[11px] font-semibold text-lais-text-muted"
        >
          <span>{{ t('TARGET_MANAGEMENT.FILTERS.METRIC') }}</span>
          <select
            v-model="metric"
            class="h-10 min-w-[190px] rounded-lais border border-lais-border bg-white px-3 text-[13px] font-semibold text-lais-text outline-none focus:border-lais-primary"
          >
            <option
              v-for="item in metricOptions"
              :key="item.value"
              :value="item.value"
            >
              {{ item.label }}
            </option>
          </select>
        </label>
        <label
          class="flex flex-col gap-1 text-[11px] font-semibold text-lais-text-muted"
        >
          <span>{{ t('TARGET_MANAGEMENT.FILTERS.PERIOD') }}</span>
          <select
            v-model="periodType"
            class="h-10 min-w-[150px] rounded-lais border border-lais-border bg-white px-3 text-[13px] font-semibold text-lais-text outline-none focus:border-lais-primary"
          >
            <option
              v-for="item in periodOptions"
              :key="item.value"
              :value="item.value"
            >
              {{ item.label }}
            </option>
          </select>
        </label>
        <label
          class="flex flex-col gap-1 text-[11px] font-semibold text-lais-text-muted"
        >
          <span>{{ t('TARGET_MANAGEMENT.FILTERS.DATE') }}</span>
          <input
            v-model="selectedDate"
            type="date"
            class="h-10 min-w-[150px] rounded-lais border border-lais-border bg-white px-3 text-[13px] font-semibold text-lais-text outline-none focus:border-lais-primary"
          />
        </label>
        <LaisPrimaryButton icon="i-lucide-plus" @click="openNewTarget">
          {{ t('TARGET_MANAGEMENT.ACTIONS.NEW_TARGET') }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          variant="ghost"
          icon="i-lucide-refresh-ccw"
          :is-loading="isLoading"
          @click="loadTargets"
        >
          {{ t('TARGET_MANAGEMENT.ACTIONS.REFRESH') }}
        </LaisPrimaryButton>
      </template>
    </LaisHeader>

    <div class="flex-1 overflow-auto px-4 py-4 sm:px-6 lg:px-10 lg:py-6">
      <div
        v-if="isForbidden"
        class="flex min-h-[420px] items-center justify-center"
      >
        <LaisCard class="max-w-[460px] text-center">
          <span
            class="i-lucide-lock mx-auto mb-3 block size-9 text-lais-text-muted"
            aria-hidden="true"
          />
          <p class="text-[16px] font-semibold text-lais-text">
            {{ t('TARGET_MANAGEMENT.STATE.FORBIDDEN') }}
          </p>
        </LaisCard>
      </div>

      <template v-else>
        <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <LaisCard
            v-for="item in summaryCards"
            :key="item.label"
            density="tight"
          >
            <p class="text-[12px] font-semibold text-lais-text-muted">
              {{ item.label }}
            </p>
            <p class="mt-2 text-[28px] font-semibold text-lais-text">
              {{ item.value }}
            </p>
          </LaisCard>
        </div>

        <div class="mt-5 flex flex-wrap items-center gap-2">
          <button
            type="button"
            class="h-10 rounded-lais border px-4 text-[13px] font-semibold transition"
            :class="
              activeTab === 'agents'
                ? 'border-lais-primary bg-lais-primary text-white'
                : 'border-lais-border bg-white text-lais-text hover:border-lais-primary'
            "
            @click="activeTab = 'agents'"
          >
            {{ t('TARGET_MANAGEMENT.TABS.AGENTS') }}
          </button>
          <button
            type="button"
            class="h-10 rounded-lais border px-4 text-[13px] font-semibold transition"
            :class="
              activeTab === 'teams'
                ? 'border-lais-primary bg-lais-primary text-white'
                : 'border-lais-border bg-white text-lais-text hover:border-lais-primary'
            "
            @click="activeTab = 'teams'"
          >
            {{ t('TARGET_MANAGEMENT.TABS.TEAMS') }}
          </button>
          <p
            class="ml-auto rounded-full bg-white px-3 py-2 text-[12px] font-semibold text-lais-text-muted ring-1 ring-lais-border"
          >
            {{
              t('TARGET_MANAGEMENT.PERIOD_RANGE', { range: currentPeriodRange })
            }}
          </p>
        </div>

        <LaisCard class="mt-4" density="flush">
          <div
            v-if="isLoading"
            class="grid gap-3 p-5"
            :aria-label="t('TARGET_MANAGEMENT.STATE.LOADING')"
          >
            <div
              v-for="item in 6"
              :key="item"
              class="h-16 rounded-lais bg-lais-bg-subtle"
            />
          </div>

          <div
            v-else-if="errorMessage"
            class="flex min-h-[220px] flex-col items-center justify-center gap-3 p-6 text-center"
          >
            <span
              class="i-lucide-circle-alert size-9 text-rose-700"
              aria-hidden="true"
            />
            <p class="text-[15px] font-semibold text-lais-text">
              {{ errorMessage }}
            </p>
            <LaisPrimaryButton
              variant="ghost"
              icon="i-lucide-refresh-ccw"
              @click="loadTargets"
            >
              {{ t('TARGET_MANAGEMENT.ACTIONS.REFRESH') }}
            </LaisPrimaryButton>
          </div>

          <div
            v-else-if="isEmpty"
            class="flex min-h-[220px] items-center justify-center p-6 text-center"
          >
            <p class="text-[15px] font-semibold text-lais-text">
              {{ t('TARGET_MANAGEMENT.STATE.EMPTY') }}
            </p>
          </div>

          <template v-else>
            <div class="hidden overflow-x-auto lg:block">
              <table class="w-full min-w-[920px] table-fixed text-left">
                <thead class="bg-lais-bg-subtle">
                  <tr>
                    <th
                      class="px-4 py-3 text-[11px] font-bold uppercase text-lais-text-muted"
                    >
                      {{ t('TARGET_MANAGEMENT.TABLE.SUBJECT') }}
                    </th>
                    <th
                      v-if="activeTab === 'teams'"
                      class="px-4 py-3 text-[11px] font-bold uppercase text-lais-text-muted"
                    >
                      {{ t('TARGET_MANAGEMENT.TABLE.MEMBERS') }}
                    </th>
                    <th
                      class="px-4 py-3 text-[11px] font-bold uppercase text-lais-text-muted"
                    >
                      {{ t('TARGET_MANAGEMENT.TABLE.ACTUAL') }}
                    </th>
                    <th
                      class="px-4 py-3 text-[11px] font-bold uppercase text-lais-text-muted"
                    >
                      {{ t('TARGET_MANAGEMENT.TABLE.TARGET') }}
                    </th>
                    <th
                      class="px-4 py-3 text-[11px] font-bold uppercase text-lais-text-muted"
                    >
                      {{ t('TARGET_MANAGEMENT.TABLE.PROGRESS') }}
                    </th>
                    <th
                      class="px-4 py-3 text-[11px] font-bold uppercase text-lais-text-muted"
                    >
                      {{ t('TARGET_MANAGEMENT.TABLE.PACE') }}
                    </th>
                    <th
                      class="px-4 py-3 text-[11px] font-bold uppercase text-lais-text-muted"
                    >
                      {{ t('TARGET_MANAGEMENT.TABLE.SOURCE') }}
                    </th>
                    <th
                      class="px-4 py-3 text-right text-[11px] font-bold uppercase text-lais-text-muted"
                    >
                      {{ t('TARGET_MANAGEMENT.TABLE.ACTIONS') }}
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-lais-border-soft">
                  <tr
                    v-for="row in rows"
                    :key="`${activeTab}-${targetDisplayName(row)}`"
                    :class="row.behind_pace ? 'bg-rose-50/50' : 'bg-white'"
                  >
                    <td class="px-4 py-4">
                      <p
                        class="truncate text-[13px] font-semibold text-lais-text"
                      >
                        {{ targetDisplayName(row) }}
                      </p>
                      <p
                        v-if="row.user?.email"
                        class="truncate text-[11px] text-lais-text-muted"
                      >
                        {{ row.user.email }}
                      </p>
                    </td>
                    <td
                      v-if="activeTab === 'teams'"
                      class="px-4 py-4 text-[13px] text-lais-text"
                    >
                      {{ row.member_count }}
                    </td>
                    <td class="px-4 py-4 text-[13px] text-lais-text">
                      {{ row.actual }}
                    </td>
                    <td class="px-4 py-4 text-[13px] text-lais-text">
                      {{ row.target?.target_value }}
                    </td>
                    <td class="px-4 py-4">
                      <div class="flex items-center gap-2">
                        <div class="h-2 w-24 rounded-full bg-lais-bg-subtle">
                          <div
                            class="h-2 rounded-full"
                            :class="
                              row.behind_pace
                                ? 'bg-rose-600'
                                : 'bg-lais-primary'
                            "
                            :style="{ width: progressWidth(row.progress) }"
                          />
                        </div>
                        <span class="text-[12px] font-semibold text-lais-text">
                          {{ formatPercent(row.progress) }}
                        </span>
                      </div>
                    </td>
                    <td class="px-4 py-4">
                      <div class="flex flex-col gap-1">
                        <span class="text-[12px] font-semibold text-lais-text">
                          {{ formatPercent(rowPace(row)) }}
                        </span>
                        <span
                          class="w-fit rounded-full px-2 py-0.5 text-[11px] font-semibold"
                          :class="
                            row.behind_pace
                              ? 'bg-rose-50 text-rose-700'
                              : 'bg-emerald-50 text-emerald-700'
                          "
                        >
                          {{ paceLabel(row) }}
                        </span>
                      </div>
                    </td>
                    <td class="px-4 py-4 text-[13px] text-lais-text">
                      {{ sourceLabel(row.target?.source) }}
                    </td>
                    <td class="px-4 py-4">
                      <div class="flex justify-end gap-2">
                        <LaisPrimaryButton
                          size="sm"
                          variant="ghost"
                          icon="i-lucide-pencil"
                          @click="openEditor(row)"
                        >
                          {{ t('TARGET_MANAGEMENT.ACTIONS.EDIT') }}
                        </LaisPrimaryButton>
                        <LaisPrimaryButton
                          v-if="activeTab === 'teams'"
                          size="sm"
                          variant="ghost"
                          icon="i-lucide-users-round"
                          @click="openBulkApply(row)"
                        >
                          {{ t('TARGET_MANAGEMENT.ACTIONS.BULK_APPLY') }}
                        </LaisPrimaryButton>
                        <LaisPrimaryButton
                          v-if="row.target?.id"
                          size="sm"
                          variant="link"
                          icon="i-lucide-rotate-ccw"
                          :disabled="isSaving"
                          @click="resetTarget(row)"
                        >
                          {{ rowActionLabel(row) }}
                        </LaisPrimaryButton>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="grid gap-3 p-4 lg:hidden">
              <LaisCard
                v-for="row in rows"
                :key="`${activeTab}-card-${targetDisplayName(row)}`"
                density="tight"
                :active="row.behind_pace"
              >
                <div class="flex items-start justify-between gap-3">
                  <div class="min-w-0">
                    <p
                      class="truncate text-[14px] font-semibold text-lais-text"
                    >
                      {{ targetDisplayName(row) }}
                    </p>
                    <p class="mt-1 text-[12px] text-lais-text-muted">
                      {{ sourceLabel(row.target?.source) }}
                    </p>
                  </div>
                  <LaisPrimaryButton
                    size="sm"
                    variant="ghost"
                    icon="i-lucide-pencil"
                    @click="openEditor(row)"
                  >
                    {{ t('TARGET_MANAGEMENT.ACTIONS.EDIT') }}
                  </LaisPrimaryButton>
                </div>
                <div
                  class="mt-4 grid grid-cols-2 gap-2 text-center sm:grid-cols-4"
                >
                  <div>
                    <p class="text-[11px] text-lais-text-muted">
                      {{ t('TARGET_MANAGEMENT.TABLE.ACTUAL') }}
                    </p>
                    <p class="text-[18px] font-semibold text-lais-text">
                      {{ row.actual }}
                    </p>
                  </div>
                  <div>
                    <p class="text-[11px] text-lais-text-muted">
                      {{ t('TARGET_MANAGEMENT.TABLE.TARGET') }}
                    </p>
                    <p class="text-[18px] font-semibold text-lais-text">
                      {{ row.target?.target_value }}
                    </p>
                  </div>
                  <div>
                    <p class="text-[11px] text-lais-text-muted">
                      {{ t('TARGET_MANAGEMENT.TABLE.PROGRESS') }}
                    </p>
                    <p class="text-[18px] font-semibold text-lais-text">
                      {{ formatPercent(row.progress) }}
                    </p>
                  </div>
                  <div>
                    <p class="text-[11px] text-lais-text-muted">
                      {{ t('TARGET_MANAGEMENT.TABLE.PACE') }}
                    </p>
                    <p class="text-[18px] font-semibold text-lais-text">
                      {{ formatPercent(rowPace(row)) }}
                    </p>
                    <p
                      class="mt-1 text-[11px] font-semibold"
                      :class="
                        row.behind_pace ? 'text-rose-700' : 'text-emerald-700'
                      "
                    >
                      {{ paceLabel(row) }}
                    </p>
                  </div>
                </div>
                <div class="mt-4 flex flex-wrap gap-2">
                  <LaisPrimaryButton
                    v-if="activeTab === 'teams'"
                    size="sm"
                    variant="ghost"
                    icon="i-lucide-users-round"
                    @click="openBulkApply(row)"
                  >
                    {{ t('TARGET_MANAGEMENT.ACTIONS.BULK_APPLY') }}
                  </LaisPrimaryButton>
                  <LaisPrimaryButton
                    v-if="row.target?.id"
                    size="sm"
                    variant="link"
                    icon="i-lucide-rotate-ccw"
                    :disabled="isSaving"
                    @click="resetTarget(row)"
                  >
                    {{ rowActionLabel(row) }}
                  </LaisPrimaryButton>
                </div>
              </LaisCard>
            </div>
          </template>
        </LaisCard>
      </template>
    </div>

    <div
      v-if="showModal"
      class="fixed inset-0 z-50 flex items-end bg-black/40 p-0 sm:items-center sm:justify-center sm:p-6"
      @click.self="closeEditor"
    >
      <form
        ref="modalPanelRef"
        role="dialog"
        aria-modal="true"
        aria-labelledby="target-modal-title"
        tabindex="-1"
        class="max-h-[92vh] w-full overflow-y-auto rounded-t-lais bg-white p-5 shadow-lais-pop sm:max-w-[520px] sm:rounded-lais"
        @submit.prevent="saveTarget"
        @keydown="handleModalKeydown"
      >
        <div class="flex items-start justify-between gap-4">
          <div class="min-w-0">
            <h2
              id="target-modal-title"
              class="text-[18px] font-semibold text-lais-text"
            >
              {{ modalTitle }}
            </h2>
            <p class="mt-1 truncate text-[13px] text-lais-text-muted">
              {{ modalSubjectLabel || t('TARGET_MANAGEMENT.MODAL.AGENT') }}
            </p>
          </div>
          <button
            type="button"
            class="i-lucide-x size-5 text-lais-text-muted"
            :aria-label="t('TARGET_MANAGEMENT.ACTIONS.CANCEL')"
            @click="closeEditor"
          />
        </div>

        <div class="mt-5 grid gap-4">
          <label
            v-if="editingContext.mode === 'new-agent'"
            class="flex flex-col gap-2 text-[12px] font-semibold text-lais-text-muted"
          >
            <span>{{ t('TARGET_MANAGEMENT.MODAL.AGENT') }}</span>
            <select
              v-model="selectedAgentId"
              class="h-11 rounded-lais border border-lais-border bg-white px-3 text-[14px] font-semibold text-lais-text outline-none focus:border-lais-primary"
              required
            >
              <option disabled value="">
                {{ t('TARGET_MANAGEMENT.ALERTS.SELECT_AGENT') }}
              </option>
              <option
                v-for="agent in agentSelectOptions"
                :key="agent.value"
                :value="agent.value"
              >
                {{ agent.label }}
              </option>
            </select>
          </label>

          <div
            v-else
            class="rounded-lais border border-lais-border bg-lais-bg-subtle px-3 py-2"
          >
            <p class="text-[11px] font-semibold text-lais-text-muted">
              {{
                editingContext.scope === 'team'
                  ? t('TARGET_MANAGEMENT.MODAL.TEAM')
                  : t('TARGET_MANAGEMENT.MODAL.AGENT')
              }}
            </p>
            <p class="mt-1 text-[14px] font-semibold text-lais-text">
              {{ modalSubjectLabel }}
            </p>
          </div>

          <div class="grid gap-3 sm:grid-cols-2">
            <label
              class="flex flex-col gap-2 text-[12px] font-semibold text-lais-text-muted"
            >
              <span>{{ t('TARGET_MANAGEMENT.MODAL.METRIC') }}</span>
              <select
                v-model="modalMetric"
                class="h-11 rounded-lais border border-lais-border bg-white px-3 text-[14px] font-semibold text-lais-text outline-none focus:border-lais-primary"
              >
                <option
                  v-for="item in metricOptions"
                  :key="item.value"
                  :value="item.value"
                >
                  {{ item.label }}
                </option>
              </select>
            </label>

            <label
              class="flex flex-col gap-2 text-[12px] font-semibold text-lais-text-muted"
            >
              <span>{{ t('TARGET_MANAGEMENT.MODAL.PERIOD') }}</span>
              <select
                v-model="modalPeriodType"
                class="h-11 rounded-lais border border-lais-border bg-white px-3 text-[14px] font-semibold text-lais-text outline-none focus:border-lais-primary"
              >
                <option
                  v-for="item in periodOptions"
                  :key="item.value"
                  :value="item.value"
                >
                  {{ item.label }}
                </option>
              </select>
            </label>
          </div>

          <div
            class="flex items-center justify-between gap-3 rounded-lais border border-lais-border bg-lais-bg-subtle px-3 py-2"
          >
            <div>
              <p class="text-[11px] font-semibold text-lais-text-muted">
                {{ t('TARGET_MANAGEMENT.MODAL.PERIOD_RANGE') }}
              </p>
              <p class="mt-1 text-[14px] font-semibold text-lais-text">
                {{ modalPeriodRange }}
              </p>
            </div>
            <div class="text-right">
              <p class="text-[11px] font-semibold text-lais-text-muted">
                {{ t('TARGET_MANAGEMENT.MODAL.SOURCE') }}
              </p>
              <span
                class="mt-1 inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1"
                :class="sourceBadgeClass(modalSource)"
              >
                {{ sourceLabel(modalSource) }}
              </span>
            </div>
          </div>

          <label
            class="flex flex-col gap-2 text-[12px] font-semibold text-lais-text-muted"
          >
            <span>{{ t('TARGET_MANAGEMENT.MODAL.TARGET_VALUE') }}</span>
            <input
              v-model.number="targetValue"
              min="0"
              step="1"
              type="number"
              :placeholder="String(modalDefaultTargetValue)"
              class="h-11 rounded-lais border border-lais-border bg-white px-3 text-[15px] font-semibold text-lais-text outline-none focus:border-lais-primary"
              required
            />
            <span class="text-[11px] font-medium text-lais-text-muted">
              {{
                t('TARGET_MANAGEMENT.MODAL.DEFAULT_HINT', {
                  value: modalDefaultTargetValue,
                })
              }}
            </span>
          </label>

          <div
            v-if="canBulkApplyFromModal"
            class="rounded-lais border border-lais-border bg-white p-3"
          >
            <label class="flex items-start gap-3">
              <input
                v-model="bulkApplyMembers"
                type="checkbox"
                class="mt-1 size-4 rounded border-lais-border text-lais-primary focus:ring-lais-primary"
              />
              <span>
                <span class="block text-[13px] font-semibold text-lais-text">
                  {{ t('TARGET_MANAGEMENT.MODAL.BULK_CHECKBOX') }}
                </span>
                <span class="mt-1 block text-[12px] text-lais-text-muted">
                  {{
                    t('TARGET_MANAGEMENT.MODAL.BULK_HELPER', {
                      count: modalRow?.member_count || 0,
                    })
                  }}
                </span>
              </span>
            </label>
          </div>

          <div
            v-if="pendingConfirmation"
            class="rounded-lais border border-amber-200 bg-amber-50 p-3"
          >
            <div class="flex items-start gap-3">
              <span
                class="i-lucide-triangle-alert mt-0.5 size-5 text-amber-700"
                aria-hidden="true"
              />
              <div class="min-w-0">
                <p class="text-[13px] font-semibold text-amber-900">
                  {{ pendingConfirmationMessage }}
                </p>
                <div class="mt-3 flex flex-wrap gap-2">
                  <LaisPrimaryButton
                    type="button"
                    size="sm"
                    variant="ghost"
                    @click="clearPendingConfirmation"
                  >
                    {{ t('TARGET_MANAGEMENT.ACTIONS.CANCEL') }}
                  </LaisPrimaryButton>
                  <LaisPrimaryButton
                    type="button"
                    size="sm"
                    icon="i-lucide-check"
                    :is-loading="isSaving"
                    @click="confirmPendingAction"
                  >
                    {{ t('TARGET_MANAGEMENT.ACTIONS.CONFIRM') }}
                  </LaisPrimaryButton>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-6 flex flex-wrap justify-end gap-2">
          <LaisPrimaryButton
            v-if="showModalReset"
            type="button"
            variant="link"
            icon="i-lucide-rotate-ccw"
            :disabled="isSaving"
            @click="resetTarget(modalRow)"
          >
            {{ rowActionLabel(modalRow) }}
          </LaisPrimaryButton>
          <LaisPrimaryButton type="button" variant="ghost" @click="closeEditor">
            {{ t('TARGET_MANAGEMENT.ACTIONS.CANCEL') }}
          </LaisPrimaryButton>
          <LaisPrimaryButton
            type="submit"
            icon="i-lucide-save"
            :is-loading="isSaving"
            :disabled="
              isSaving ||
              (editingContext.mode === 'new-agent' && !selectedAgentId)
            "
          >
            {{ t('TARGET_MANAGEMENT.ACTIONS.SAVE') }}
          </LaisPrimaryButton>
        </div>
      </form>
    </div>
  </section>
</template>
